import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PIService } from '@/services/api';
import type { PrincipalInvestigator } from '@/types';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// Configuration for Typesense - ideally from environment variables
// IMPORTANT: Exposing API keys client-side is a security risk.
// For production, use search-only API keys or proxy through your backend.
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST || 'typesense';
const TYPESENSE_PORT = process.env.REACT_APP_TYPESENSE_PORT || '8108';
const TYPESENSE_PROTOCOL = process.env.REACT_APP_TYPESENSE_PROTOCOL || 'http';
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY || 'ebkwjebrkjvkjh2v4kjhisvjb3'; // Use the API key from .env
const TYPESENSE_COLLECTION_NAME = 'pis'; // Or your actual collection name

const Search: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [results, setResults] = useState<PrincipalInvestigator[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Function to search through Django API as a fallback
  const searchWithDjangoAPI = async (query: string) => {
    try {
      const data = await PIService.search(query);
      return data;
    } catch (err) {
      console.error("API search fallback failed:", err);
      return [];
    }
  };
  
  useEffect(() => {
    // Debounce search function
    const handler = setTimeout(async () => {
      if (!searchQuery.trim()) {
        setResults([]);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Note: Using fetch directly. For a better DX, consider `typesense-js` library.
        const searchParameters = {
          'q': searchQuery,
          'query_by': 'name,institution_name,research_field', // Adjust fields as needed
          'per_page': '10' // Adjust how many results you want, ensure it's a string for URLSearchParams
        };
        const queryParams = new URLSearchParams(searchParameters).toString();
        const response = await fetch(
          `${TYPESENSE_PROTOCOL}://${TYPESENSE_HOST}:${TYPESENSE_PORT}/collections/${TYPESENSE_COLLECTION_NAME}/documents/search?${queryParams}`,
          {
            method: 'GET',
            headers: {
              'X-TYPESENSE-API-KEY': TYPESENSE_API_KEY,
            },
          }
        );

        if (!response.ok) {
          // Try Django API as fallback if Typesense fails
          console.log("Typesense search failed, falling back to Django API");
          const apiResults = await searchWithDjangoAPI(searchQuery);
          setResults(apiResults);
          
          if (apiResults.length === 0) {
            setError('No results found. Try a different search term.');
          }
          return;
        }

        const data = await response.json();
        // Assuming PI data is in `hit.document`
        const fetchedPIs = data.hits?.map((hit: any) => hit.document as PrincipalInvestigator) || [];
        setResults(fetchedPIs);

        if (fetchedPIs.length === 0) {
          setError('No results found. Try a different search term.');
        }
      } catch (err: any) {
        // Try Django API as fallback if Typesense fails
        console.log("Typesense search error, falling back to Django API:", err);
        const apiResults = await searchWithDjangoAPI(searchQuery);
        setResults(apiResults);
        
        if (apiResults.length === 0) {
          setError('No results found. Try a different search term.');
        }
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]); // Re-run effect when searchQuery changes
  
  // Display a PI result card
  const PiCard = ({ pi }: { pi: PrincipalInvestigator }) => (
    <Link 
      to={`/pi/${pi.id}`}
      className="block p-6 transition-shadow bg-white border shadow-sm rounded-xl border-neutral-200 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-neutral-800">{pi.name}</h3>
          <p className="text-neutral-600">{pi.institution_name}</p>
          {pi.research_field && (
            <p className="mt-1 text-sm text-neutral-500">{pi.research_field}</p>
          )}
        </div>
        
        <div className="text-right">
          <div className="flex items-center space-x-1">
            <span className="font-semibold">{pi.total_ratings}</span>
            <span className="text-sm text-neutral-500">ratings</span>
          </div>
          
          {pi.h_index > 0 && (
            <div className="mt-1 text-sm text-neutral-500">
              h-index: {pi.h_index}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-between mt-4">
        <div>
          <div className="text-sm font-medium">Egg Score</div>
          <div className="w-full h-4 mt-1 overflow-hidden rounded-full bg-neutral-200">
            <div 
              className="h-full bg-primary-500" 
              style={{ width: `${pi.avg_egg_score}%` }}
            />
          </div>
          <div className="mt-1 text-sm text-neutral-500">
            {Math.round(pi.avg_egg_score)}%
          </div>
        </div>
      </div>
    </Link>
  );
  
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="mb-8 text-3xl font-bold text-center">
        Find a Principal Investigator
      </h1>
      
      {/* Search Input - Form submission is no longer the primary search trigger */}
      <div className="mb-8">
        <div className="relative">
          <input
            type="text"
            className="py-3 pl-12 text-lg input"
            placeholder="Start typing to search for PIs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search Principal Investigators"
          />
          <MagnifyingGlassIcon className="h-6 w-6 text-neutral-400 absolute left-4 top-3.5" />
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="p-4 mb-6 border rounded-lg bg-primary-50 border-primary-200 text-neutral-700">
          {error}
        </div>
      )}
      
      {/* Results */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="py-12 text-center">
            <div className="w-12 h-12 mx-auto border-b-2 rounded-full animate-spin border-primary-600"></div>
            <p className="mt-4 text-neutral-600">Searching for PIs...</p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold">
              {searchQuery && results.length > 0 ? `Search Results (${results.length})` : 
               searchQuery && !isLoading ? 'No Results' : 
               'Type to search for Principal Investigators'}
            </h2>
            
            {results.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-1">
                {results.map(pi => (
                  <PiCard key={pi.id} pi={pi} />
                ))}
              </div>
            ) : (
              !isLoading && searchQuery && ( // Only show "No results found" if a search was made
              <div className="py-8 text-center text-neutral-500">
                  No results found for "{searchQuery}".
              </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Search; 